import React, { useState, useMemo } from "react";
import { Box, Grid, Stack, useTheme, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, FormControl, InputLabel, Select, MenuItem, Typography } from "@mui/material";
import { RiskBreakdownChart, GaugeChart, RadarChart } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { riskBreakdown, riskTotal, riskParticipationRate, radarComparison, gaugeData, atRiskUsers, cardiovascularJeopardyUsers } from "../data";

export const JeopardyInsights: React.FC = () => {
  const theme = useTheme();
  const [riskFilter, setRiskFilter] = useState<string>("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const riskChipColor = (level: string) => {
    if (level === "High") return { bg: theme.palette.error.main, text: "#fff" };
    if (level === "Medium") return { bg: theme.palette.warning.main, text: "#fff" };
    return { bg: theme.palette.success.main, text: "#fff" };
  };

  const cvStatusColor = (status: string) => {
    if (status === "red") return theme.palette.error.main;
    if (status === "amber") return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const filteredUsers = useMemo(() =>
    riskFilter === "All" ? atRiskUsers : atRiskUsers.filter((u) => u.risk_level === riskFilter),
    [riskFilter],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="Risk Breakdown" subtitle="Overall risk distribution across the organisation.">
              <RiskBreakdownChart data={riskBreakdown} total={riskTotal} participationRate={riskParticipationRate} />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard title="Overall Wellbeing" subtitle="Composite wellbeing gauge score.">
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <GaugeChart value={gaugeData.overallWellbeing} min={0} max={100} unit="%" title="Overall Wellbeing" height={200} />
              </Box>
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard title="Recovery Readiness" subtitle="How ready the workforce is to perform.">
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <GaugeChart value={gaugeData.recoveryReadiness} min={0} max={100} unit="%" title="Recovery Readiness" height={200} />
              </Box>
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard title="HRV Baseline" subtitle="Average heart rate variability baseline.">
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <GaugeChart value={52} min={0} max={100} unit="ms" title="HRV Baseline" height={200} baseline={{ min: 40, max: 70 }} useNeutralColors />
              </Box>
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="Measure Comparison" subtitle="Current period vs previous period across key metrics.">
              <RadarChart
                data={radarComparison}
                seriesKeys={["current", "previous"]}
                seriesLabels={["Current Period", "Previous Period"]}
                metricKey="metric"
                height={400}
                colors={[theme.palette.primary.main, theme.palette.secondary.main]}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="At-Risk Participants" subtitle="Participants identified as requiring attention based on biometric data.">
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Risk Level</InputLabel>
                  <Select value={riskFilter} label="Risk Level" onChange={(e) => { setRiskFilter(e.target.value); setPage(0); }}>
                    <MenuItem value="All">All Levels</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Low">Low</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">{filteredUsers.length} participant{filteredUsers.length !== 1 ? "s" : ""}</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Risk Level</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Risk Score</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Recovery %</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Sleep %</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Strain</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">HRV (ms)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Days in Red</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((u) => {
                      const chipColor = riskChipColor(u.risk_level);
                      return (
                        <TableRow key={u.name} hover>
                          <TableCell>{u.name}</TableCell>
                          <TableCell>
                            <Chip label={u.risk_level} size="small" sx={{ bgcolor: chipColor.bg, color: chipColor.text, fontWeight: 600, fontSize: "0.7rem" }} />
                          </TableCell>
                          <TableCell align="right">{u.risk_score}</TableCell>
                          <TableCell align="right">{u.recovery}%</TableCell>
                          <TableCell align="right">{u.sleep}%</TableCell>
                          <TableCell align="right">{u.strain}</TableCell>
                          <TableCell align="right">{u.hrv}</TableCell>
                          <TableCell align="right">{u.days_in_red}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredUsers.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[5, 10]}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="Cardiovascular Jeopardy" subtitle="Participants with sustained low HRV and elevated resting heart rate.">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Avg HRV (ms)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Avg Resting HR (bpm)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cardiovascularJeopardyUsers.map((u) => (
                      <TableRow key={u.name} hover>
                        <TableCell>{u.name}</TableCell>
                        <TableCell>{u.department}</TableCell>
                        <TableCell align="right">{u.avg_hrv}</TableCell>
                        <TableCell align="right">{u.avg_resting_hr}</TableCell>
                        <TableCell>
                          <Chip label={u.status.toUpperCase()} size="small" sx={{ bgcolor: cvStatusColor(u.status), color: "#fff", fontWeight: 600, fontSize: "0.7rem" }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
